import { useQuery } from '@tanstack/react-query'
import { Card } from '@/components/ui/card'
import { getFearGreedIndex } from '@/api/endpoints/fear-greed'
import { motion } from 'framer-motion'
import { Activity, TrendingUp, TrendingDown, Minus, Info } from 'lucide-react'
import { useState } from 'react'

const getClassificationColor = (classification: string): string => {
  switch (classification.toLowerCase()) {
    case 'extreme fear':
      return 'text-red-500'
    case 'fear':
      return 'text-orange-500'
    case 'neutral':
      return 'text-yellow-500'
    case 'greed':
      return 'text-green-500'
    case 'extreme greed':
      return 'text-emerald-500'
    default:
      return 'text-gray-500'
  }
}

const getClassificationBg = (classification: string): string => {
  switch (classification.toLowerCase()) {
    case 'extreme fear':
      return 'bg-red-500/10 border-red-500/20'
    case 'fear':
      return 'bg-orange-500/10 border-orange-500/20'
    case 'neutral':
      return 'bg-yellow-500/10 border-yellow-500/20'
    case 'greed':
      return 'bg-green-500/10 border-green-500/20'
    case 'extreme greed':
      return 'bg-emerald-500/10 border-emerald-500/20'
    default:
      return 'bg-gray-500/10 border-gray-500/20'
  }
}

const getClassificationIcon = (classification: string) => {
  switch (classification.toLowerCase()) {
    case 'extreme fear':
    case 'fear':
      return TrendingDown
    case 'extreme greed':
    case 'greed':
      return TrendingUp
    default:
      return Minus
  }
}

const getGaugeColor = (value: number): string => {
  if (value <= 20) return '#ef4444'
  if (value <= 40) return '#f97316'
  if (value <= 60) return '#eab308'
  if (value <= 80) return '#22c55e'
  return '#10b981'
}

export function FearGreedWidget() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['fear-greed'],
    queryFn: getFearGreedIndex,
    refetchInterval: 5 * 60 * 1000,
  })

  const [showInfo, setShowInfo] = useState(false)

  if (isLoading) {
    return (
      <Card className="p-6 bg-gradient-to-br from-card to-muted/50 border-0 shadow-xl">
        <div className="flex items-center justify-center h-64">
          <div className="relative">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary border-t-transparent"></div>
            <div className="absolute inset-0 rounded-full border-2 border-primary/20"></div>
          </div>
        </div>
      </Card>
    )
  }

  if (error || !data) {
    return (
      <Card className="p-6 bg-gradient-to-br from-card to-muted/50 border-0 shadow-xl">
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground space-y-4">
          <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
            <Activity className="h-8 w-8 text-muted-foreground/50" />
          </div>
          <p className="text-sm">Failed to load Fear & Greed Index</p>
        </div>
      </Card>
    )
  }

  const Icon = getClassificationIcon(data.value_classification)
  const colorClass = getClassificationColor(data.value_classification)
  const bgClass = getClassificationBg(data.value_classification)
  const gaugeColor = getGaugeColor(data.value)

  return (
    <Card className="p-6 bg-gradient-to-br from-card to-muted/50 border-0 shadow-xl overflow-hidden relative">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-primary/5 to-transparent rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      
      {/* Header */}
      <div className="flex items-center justify-between mb-8 relative z-10">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-xl">
            <Activity className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Fear & Greed Index</h3>
            <p className="text-xs text-muted-foreground">Crypto Market Sentiment</p>
          </div>
        </div>
        <button
          onClick={() => setShowInfo(!showInfo)}
          className="p-2 hover:bg-muted rounded-lg transition-colors"
        >
          <Info className="h-4 w-4 text-muted-foreground" />
        </button>
      </div>

      {/* Main Content */}
      <div className="flex flex-col items-center relative z-10">
        {/* Modern Circular Gauge */}
        <div className="relative w-48 h-48 mb-6">
          {/* Outer ring */}
          <svg viewBox="0 0 200 200" className="w-full h-full transform -rotate-90">
            {/* Track */}
            <circle
              cx="100"
              cy="100"
              r="85"
              fill="none"
              stroke="currentColor"
              strokeWidth="12"
              className="text-muted/30"
            />
            
            {/* Progress arc with gradient */}
            <defs>
              <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={gaugeColor} stopOpacity="0.8" />
                <stop offset="100%" stopColor={gaugeColor} />
              </linearGradient>
              <filter id="glow">
                <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            
            <motion.circle
              cx="100"
              cy="100"
              r="85"
              fill="none"
              stroke="url(#progressGradient)"
              strokeWidth="12"
              strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 85}`}
              initial={{ strokeDashoffset: `${2 * Math.PI * 85}` }}
              animate={{ strokeDashoffset: `${2 * Math.PI * 85 * (1 - data.value / 100)}` }}
              transition={{ duration: 1.5, ease: "easeOut" }}
              filter="url(#glow)"
              style={{ transformOrigin: 'center' }}
            />
          </svg>
          
          {/* Center content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
              className="text-center"
            >
              <div className={`text-5xl font-bold ${colorClass}`}>
                {data.value}
              </div>
              <div className="text-xs text-muted-foreground mt-1">out of 100</div>
            </motion.div>
          </div>
          
          {/* Decorative dots */}
          {[0, 25, 50, 75, 100].map((mark, index) => {
            const angle = (mark / 100) * 360 - 90
            const rad = (angle * Math.PI) / 180
            const x = 100 + 70 * Math.cos(rad)
            const y = 100 + 70 * Math.sin(rad)
            return (
              <motion.circle
                key={mark}
                cx={x}
                cy={y}
                r="3"
                className="fill-muted-foreground/30"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.8 + index * 0.1 }}
              />
            )
          })}
        </div>

        {/* Classification Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-full border ${bgClass}`}
        >
          <Icon className={`h-5 w-5 ${colorClass}`} />
          <span className={`text-lg font-semibold capitalize ${colorClass}`}>
            {data.value_classification}
          </span>
        </motion.div>

        {/* Scale indicators */}
        <div className="flex justify-between w-full max-w-xs mt-8 px-4">
          {[
            { label: 'Extreme Fear', color: 'text-red-500', value: 0 },
            { label: 'Neutral', color: 'text-yellow-500', value: 50 },
            { label: 'Extreme Greed', color: 'text-emerald-500', value: 100 }
          ].map((item) => (
            <div key={item.value} className="text-center">
              <div className={`text-xs font-medium ${item.color}`}>{item.value}</div>
              <div className="text-[10px] text-muted-foreground mt-0.5">{item.label}</div>
            </div>
          ))}
        </div>

        {/* Last updated */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-6 text-xs text-muted-foreground flex items-center gap-1"
        >
          <span>Updated: {new Date(data.timestamp).toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}</span>
        </motion.div>
      </div>

      {/* Info tooltip */}
      {showInfo && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 p-4 bg-muted/50 rounded-xl text-sm text-muted-foreground"
        >
          <p className="mb-2">
            <strong className="text-foreground">What is this?</strong>
          </p>
          <p>
            The Fear & Greed Index measures market sentiment. Extreme fear can indicate buying opportunities, 
            while extreme greed may signal a market correction.
          </p>
        </motion.div>
      )}
    </Card>
  )
}